import { getQueryCommentsForMedicalPaOrder } from "../../../../api/firebase/firestoreService";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { sortTableHeadersByOrder } from "../../../../components/custom-table/utils/sorting";
import { HOLD_TEXT_UNTIL } from "../../../../constants/stringLiterals";
import { Hit } from "../../../../data-model/algoliaSearchModel";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { MedicalPaOrderStatus } from "../../../../enums/medicalPaOrderStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../utils/stringModifications";
import { getOrderIcons } from "./getOrderIcons";

/** Width freed by dropping RPA status columns for Sunstate (RPA_STATUS 4% + RPA_STATUS_TWO 3%). */
const SUNSTATE_RPA_COLUMNS_WIDTH = 7;

/** Column key identifiers for the medical PA orders table, mapping to table cell data properties. */
export enum MedicalPaOrdersAlgoliaTableColumnKeys {
  SELECT = "select",
  PATIENT_DETAILS = "patientDetails",
  CREATED_AT = "createdAt",
  DATE_OF_WORK = "dateOfWork",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  PROVIDER = "provider",
  DATE_OF_SERVICE = "dateOfService",
  BO_VALUE = "boValue",
  AUTH_VERIFICATION = "authVerification",
  RPA_STATUS = "rpaStatusForAlgolia",
  ID = "id",
  ROW_DATA = "rowData",
  ASSIGNEE = "assignee",
  RPA_STATUS_TWO = "rpaStatusTwoForAlgolia",
  ORDER_PROCESS_AND_REVIEW_STATUS = "orderProcessAndReviewStatus",
  /** Opens the status-log / notes side panel (same behaviour as My PA Orders). */
  SIDE_MODAL_ICON = "sideModalIcon",
  LOCATION = "location",
  APPOINTMENT_TYPE = "appointmentType",
  BO_VALUE_WITH_RADIO_SELECT = "boValueWithRadioSelect",
}

/**
 * Canonical API field names used for server-side sorting on the medical PA orders table.
 * Values must match the `sort_by` parameter accepted by the Postgres search endpoint.
 */
export enum MedicalPaOrdersAlgoliaSortFields {
  FIRST_NAME = "first_name",
  CREATED_AT = "created_at",
  DATE_OF_WORK = "date_of_work",
  PRIMARY_PAYER_NAME = "primary_payer_name",
  REGIMEN_NAME = "regimen_name",
  PRACTITIONER_NAME = "practitioner_name",
  DATE_OF_SERVICE = "date_of_service",
}

export const MedicalPaOrdersAlgoliaTableHeadersForAdmin: TableHeader[] = [
  {
    label: "All",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.SELECT,
    order: 0,
    width: 3,
    type: TableCellType.CHECKBOX,
  },
  {
    label: "Patient Name",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 8,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
  },
  {
    label: "Date of Creation",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT,
    order: 2,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
  },
  {
    label: "Date of Work",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK,
    order: 3,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
  },

  {
    label: "Plan Details",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS,
    order: 4,
    width: 9,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
  },
  {
    label: "Medication",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION,
    order: 5,
    width: 12,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
  },
  {
    label: "Provider",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
  },
  {
    label: "DOS",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE,
    order: 7,
    width: 7,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
  },
  {
    label: "BO Value",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE,
    order: 8,
    width: 7,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION,
    order: 9,
    width: 7,
    type: TableCellType.BADGE_WITH_STATUS_ICON,
  },
  {
    label: "Assigned To",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE,
    order: 10,
    width: 8,
    type: TableCellType.CLICKABLE_BADGE,
    subKey: "text",
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS,
    order: 11,
    width: 4,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO,
    order: 12,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
    order: 13,
    width: 5,
    type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON,
    order: 14,
    width: 3,
    type: TableCellType.SIDE_MODAL_ICON,
  },
];

export const MedicalPaOrdersAlgoliaTableHeadersForNonAdmin: TableHeader[] = [
  {
    label: "Patient Name",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
  },
  {
    label: "Date of Creation",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT,
    order: 2,
    width: 9,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
  },
  {
    label: "Date of Work",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
  },
  {
    label: "Plan Details",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS,
    order: 4,
    width: 9,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
  },
  {
    label: "Medication",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION,
    order: 5,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
  },
  {
    label: "Provider",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER,
    order: 6,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
  },
  {
    label: "DOS",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE,
    order: 7,
    width: 7,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
  },
  {
    label: "BO Value",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE,
    order: 8,
    width: 7,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION,
    order: 9,
    width: 7,
    type: TableCellType.BADGE_WITH_STATUS_ICON,
  },
  {
    label: "Assigned To",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE,
    order: 10,
    width: 8,
    type: TableCellType.STRING,
    subKey: "text",
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS,
    order: 11,
    width: 4,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO,
    order: 12,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
    order: 13,
    width: 5,
    type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
  },
  {
    label: "",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON,
    order: 14,
    width: 3,
    type: TableCellType.SIDE_MODAL_ICON,
  },
];

/** Replaces the Medication column header with an Appointment Type column in the same slot. */
const replaceMedicationWithAppointmentType = (
  header: TableHeader,
): TableHeader =>
  header.key === MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION
    ? {
        label: "Appointment Type",
        key: MedicalPaOrdersAlgoliaTableColumnKeys.APPOINTMENT_TYPE,
        order: header.order,
        width: header.width,
        type: TableCellType.STRING,
      }
    : header;

/**
 * For urology exception-flow orgs only: replaces the plain "BO Value" badge column with the
 * "Estimate" clickable column (`BO_VALUE_WITH_RADIO_SELECT` +
 * {@link TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK}). Default admin/non-admin headers
 * use {@link MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE} + {@link TableCellType.BADGE}
 * for all other orgs; {@link buildSunstateUrologyHeaders} applies this transform only on
 * the exception-flow header set.
 *
 * @param header - Original table header entry to transform
 * @returns The Estimate header when the slot was BO Value, otherwise the input unchanged
 */
const replaceBoValueWithEstimateForSunstateUrology = (
  header: TableHeader,
): TableHeader =>
  header.key === MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE
    ? {
        label: "Estimate",
        key: MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT,
        order: header.order,
        width: header.width,
        type: TableCellType.CLICKABLE_BADGE_WITH_ON_CLICK,
      }
    : header;

/**
 * Urology exception-flow header pipeline: keeps Date of Work, inserts Location immediately
 * after it, replaces Medication with Appointment Type, swaps BO Value for Estimate for
 * both admin and non-admin, removes the two trailing RPA icon columns (not the notes
 * side-modal column), and reassigns
 * `order` so the column list stays contiguous. Widths remain at 100% (freed RPA width is assigned to
 * Location).
 *
 * @param headers - Base admin or non-admin header array
 * @returns Transformed headers for exception-flow orgs
 */
const buildSunstateUrologyHeaders = (headers: TableHeader[]): TableHeader[] => {
  const withoutRpa = headers.filter(
    (h) =>
      h.key !== MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS &&
      h.key !== MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO,
  );
  const withAppointmentType = withoutRpa.map(
    replaceMedicationWithAppointmentType,
  );
  const withEstimate = withAppointmentType.map(
    replaceBoValueWithEstimateForSunstateUrology,
  );
  const sorted = sortTableHeadersByOrder(withEstimate);
  const dateOfWorkIndex = sorted.findIndex(
    (h) => h.key === MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK,
  );
  if (dateOfWorkIndex < 0) {
    return sorted.map((header, index) => ({ ...header, order: index }));
  }
  const locationHeader: TableHeader = {
    label: "Location",
    key: MedicalPaOrdersAlgoliaTableColumnKeys.LOCATION,
    order: -1,
    width: SUNSTATE_RPA_COLUMNS_WIDTH,
    type: TableCellType.STRING,
  };
  const withLocation = [
    ...sorted.slice(0, dateOfWorkIndex + 1),
    locationHeader,
    ...sorted.slice(dateOfWorkIndex + 1),
  ];
  return withLocation.map((header, index) => ({
    ...header,
    order: index,
  }));
};

/**
 * Returns the medical PA orders Algolia table headers for the current viewer.
 *
 * Resolves the base header set from `isAdmin`
 * (`MedicalPaOrdersAlgoliaTableHeadersForAdmin` vs
 * `MedicalPaOrdersAlgoliaTableHeadersForNonAdmin`) and then applies any
 * org-specific column overrides. For orgs in `exceptionFlowForUrologyOrgs`: Date of Work
 * is kept, Location is added after it, Medication becomes Appointment Type, the BO Value
 * badge column becomes the Estimate clickable column, and the two RPA icon columns are
 * removed — widths still total 100%. The notes {@link MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON}
 * column remains. All other orgs receive the base header set unchanged.
 *
 * @param isAdmin - Whether the current user has admin privileges
 * @param orgId - Organization ID for scoping org-specific column overrides
 * @returns Table headers to render for this user + org combination
 */
export const getMedicalPaOrdersAlgoliaTableHeaders = (
  isAdmin: boolean,
  orgId: string,
): TableHeader[] => {
  const baseHeaders = isAdmin
    ? MedicalPaOrdersAlgoliaTableHeadersForAdmin
    : MedicalPaOrdersAlgoliaTableHeadersForNonAdmin;

  if (!isExceptionFlowForUrologyOrg(orgId)) {
    return baseHeaders;
  }

  return buildSunstateUrologyHeaders(baseHeaders);
};

/**
 * Transforms raw Postgres/Algolia Hit[] into table row objects for admin users.
 * Includes a select checkbox column, clickable assignee badge, full RPA status columns,
 * and the status-log notes side-modal icon column. The `BO_VALUE_WITH_RADIO_SELECT`
 * cell is backed by `estimate_value` so orgs that swap the BO column to Estimate
 * (e.g. Sunstate Urology) render the editable estimate amount.
 *
 * @param data - Array of order Hit objects from the search response
 * @param authStatusOptions - Auth status options for rendering status badges
 * @returns Array of table row objects keyed by MedicalPaOrdersAlgoliaTableColumnKeys
 */
export const generateMedicalPaOrdersAlgoliaTableDataForAdmin = (
  data: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return data.map((item) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.auth_status,
    );

    const textToDisplay = `${HOLD_TEXT_UNTIL} ${formatDateToUTCWithFormat(item?.date_of_hold_until_iso ?? "", "MM/DD/YYYY", "--")}`;

    return {
      [MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]: {
        id: item?.id ?? "",
        label: "",
        isChecked: false,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(item?.first_name ?? "", "", item?.last_name ?? ""),
        ),
        secondaryText: `MRN : ${item?.patient_id ?? ""}`,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          item?.date_of_service_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK]:
        item?.date_of_work_iso ?? null,
      [MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS]: {
        mainText: item?.primary_payer_name ?? "",
        secondaryText: item?.primary_member_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION]:
        item?.regimen_name ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA]: JSON.stringify(item),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ID]: item?.id ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT]:
        formatDateToUTCWithFormat(
          item?.created_at_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === MedicalPaOrderStatus.HOLD ||
          authStatus?.id === MedicalPaOrderStatus.QUERY,
        displayText: textToDisplay,
        fetchFn:
          authStatus?.id === MedicalPaOrderStatus.QUERY
            ? () => getQueryCommentsForMedicalPaOrder(item?.id ?? "")
            : undefined,
        submissionStatus: item?.submission_status,
        orderId: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE]: {
        text: item?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS]: {
        status: item?.ev_write_back_status ?? EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
        masterAuthStatus: item?.master_auth_status ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          item?.document_upload_status ?? DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
        masterAuthStatus: item?.master_auth_status ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        item?.practitioner_name ?? "",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE]: {
        text: item?.assigned_to_name ?? "",
        id: item?.id ?? "",
        shouldLogEvent: true,
        assignedToId: item?.assigned_to ?? "",
        metaData: {
          patient_id: item?.patient_id ?? "",
        },
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          item?.medical_order_status as MedicalOrderStatus,
          item?.medical_order_review_status as MedicalOrderReviewStatus,
          item?.master_auth_status === "query" &&
            item?.ai_agent_type === "agent",
        ),
        order_id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON]: {
        id: item?.id ?? "",
        status: "others",
        borderColor: item?.notes_color ?? "",
        isNotesPresent: item?.notes_present ?? false,
        patientId: item?.patient_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.LOCATION]: item?.location ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.APPOINTMENT_TYPE]:
        item?.regimen_name ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT]: {
        value: item?.estimate_value ?? "Required",
        id: item?.id ?? "",
      },
    };
  });
};

/**
 * Transforms raw Postgres/Algolia Hit[] into table row objects for non-admin users.
 * Omits the select checkbox column and shows assignee as plain text instead of a clickable badge.
 * Includes `BO_VALUE_WITH_RADIO_SELECT` (backed by `estimate_value`) so orgs that swap the BO column
 * to Estimate (e.g. Sunstate Urology) have the same cell shape as the admin generator, and the notes
 * side-modal icon payload.
 *
 * @param data - Array of order Hit objects from the search response
 * @param authStatusOptions - Auth status options for rendering status badges
 * @returns Array of table row objects keyed by MedicalPaOrdersAlgoliaTableColumnKeys
 */
export const generateMedicalPaOrdersAlgoliaTableDataForNonAdmin = (
  data: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return data.map((item) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.auth_status,
    );
    const textToDisplay = `${HOLD_TEXT_UNTIL} ${formatDateToUTCWithFormat(item?.date_of_hold_until_iso ?? "", "MM/DD/YYYY", "--")}`;
    return {
      [MedicalPaOrdersAlgoliaTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(item?.first_name ?? "", "", item?.last_name ?? ""),
        ),
        secondaryText: `MRN : ${item?.patient_id ?? ""}`,
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          item?.date_of_service_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.DATE_OF_WORK]:
        item?.date_of_work_iso ?? null,
      [MedicalPaOrdersAlgoliaTableColumnKeys.PLAN_DETAILS]: {
        mainText: item?.primary_payer_name ?? "",
        secondaryText: item?.primary_member_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.MEDICATION]:
        item?.regimen_name ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA]: JSON.stringify(item),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ID]: item?.id ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.CREATED_AT]:
        formatDateToUTCWithFormat(
          item?.created_at_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE]: {
        text: item?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.BO_VALUE_WITH_RADIO_SELECT]: {
        value: item?.estimate_value ?? "Required",
        id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === MedicalPaOrderStatus.HOLD ||
          authStatus?.id === MedicalPaOrderStatus.QUERY,
        displayText: textToDisplay,
        fetchFn:
          authStatus?.id === MedicalPaOrderStatus.QUERY
            ? () => getQueryCommentsForMedicalPaOrder(item?.id ?? "")
            : undefined,
        submissionStatus: item?.submission_status,
        orderId: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        item?.practitioner_name ?? "",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.ASSIGNEE]: capitalizeString(
        item?.assigned_to_name ?? "--",
      ),
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS]: {
        status: item?.ev_write_back_status ?? EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
        masterAuthStatus: item?.master_auth_status ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          item?.document_upload_status ?? DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: item?.id ?? "",
        masterAuthStatus: item?.master_auth_status ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          item?.medical_order_status as MedicalOrderStatus,
          item?.medical_order_review_status as MedicalOrderReviewStatus,
          item?.master_auth_status === "query" &&
            item?.ai_agent_type === "agent",
        ),
        order_id: item?.id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON]: {
        id: item?.id ?? "",
        status: "others",
        borderColor: item?.notes_color ?? "",
        isNotesPresent: item?.notes_present ?? false,
        patientId: item?.patient_id ?? "",
      },
      [MedicalPaOrdersAlgoliaTableColumnKeys.LOCATION]: item?.location ?? "",
      [MedicalPaOrdersAlgoliaTableColumnKeys.APPOINTMENT_TYPE]:
        item?.regimen_name ?? "",
    };
  });
};
